/**
 * The pool of names the draft board searches.
 *
 * Draft night is ten people typing under time pressure, so search has to forgive: initials
 * ("cmc"), last name only, a misremembered first name, a missing apostrophe. It also has to be
 * fast enough to run on every keystroke over ~1000 players without a debounce, which rules out
 * anything clever and rules in a precomputed lowercase key per player.
 *
 * The bank ships as a build-time snapshot from nflverse (accurate, but its CDN sends no CORS
 * header so a browser cannot read it) and can be refreshed at runtime from Sleeper (less tidy,
 * but browser-readable). Bye weeks come from the schedule either way — they do not change once
 * the season is set, so the snapshot's copy stays correct even after a roster refresh.
 */

import bundled from '../data/players-2026.json';
import {
  cleanPlayerName, feedKey, fullerName, hasWildcard, matcher, sameFeedPlayer,
} from './names.js';

export interface Player {
  name: string;
  /** Primary position — QB, RB, WR, TE, K, DEF. What the board colours the chip by. */
  pos: string;
  /**
   * Every position he may be started at, primary included, where that is more than one.
   *
   * Yahoo decides eligibility and this matters in a keeper league: a tight end who also qualifies
   * at receiver is a different asset from one who does not, and filtering the board to TE has to
   * show him. Absent for the ordinary player, who is only one thing.
   */
  elig?: string[];
  /** Empty for a free agent. He is not on a team, so the board must not print one. */
  team: string;
  /**
   * For a free agent: the club he was last with.
   *
   * Kept apart from `team` deliberately. "Nick Chubb, RB, last with Houston" helps somebody find
   * him; "Nick Chubb, HOU" reads as a Texan and gets him drafted as one. The old bank put the
   * last club straight into `team` and every screen believed it.
   */
  last?: string;
  bye: number | null;
  /**
   * Availability: ACT on a roster, IR / PUP / EXE / SUS / PS if he is on one but not playing,
   * FA if he is not on a roster at all, RET if he has retired.
   *
   * Everybody is in the bank, including the cut and the unsigned. A GM whose target tore an ACL
   * in camp still wants to see the name and decide for himself, and somebody always drafts a free
   * agent in the last round. Hiding them made the board look authoritative and be wrong.
   */
  status?: string;
  /**
   * His number, when the row came from a current-season roster feed.
   *
   * Carried for the draft room, and used as PROVENANCE by `collapseIdentities`: only the roster
   * pass has jersey numbers, so a row that has one is the row that is right about his team.
   */
  jersey?: number;
  /** Present only on a runtime refresh — how the bank knows it is newer than the snapshot. */
  refreshedAt?: string;
}

/**
 * Statuses that mean "on somebody's 2026 roster". Injured, suspended and exempt all count — they
 * are on a team, they have a bye, and they get drafted.
 */
const ON_ROSTER = new Set(['ACT', 'IR', 'PUP', 'EXE', 'SUS']);

/** Free agent, camp cut, practice squad, retired — in the bank, but not on a roster. */
export const isUnsigned = (p: Player) => !ON_ROSTER.has(p.status ?? 'ACT');

/**
 * What to print in a team column.
 *
 * A free agent gets no team, because he has none — that was the whole bug. But his last club is
 * still how people find him in a list of six hundred receivers, so it shows as "ex HOU": short,
 * and impossible to mistake for a current team the way a bare "HOU" was.
 */
export function teamLabel(p: Player): string {
  if (p.team) return p.team;
  return p.last ? `ex ${p.last}` : '—';
}

/** How to label a non-active player on the board. Empty for the ordinary case. */
export const STATUS_LABEL: Readonly<Record<string, string>> = {
  IR: 'IR', PUP: 'PUP', EXE: 'exempt', SUS: 'suspended',
  FA: 'free agent', CUT: 'cut', PS: 'practice squad', RET: 'retired',
};

export interface PlayerBank {
  season: number;
  /** ISO date of the snapshot, or of the last successful refresh. */
  asOf: string;
  source: string;
  players: Player[];
  byes: Record<string, number>;
}

interface Raw {
  n: string; p: string; t: string; b: number | null; st?: string; last?: string; e?: string[];
  j?: number;
}

/** Every position a player may be started at — the list, whether or not he has more than one. */
export const positionsOf = (p: Player): string[] => (p.elig?.length ? p.elig : [p.pos]);

/** Does he qualify here? The question every position filter on the board is really asking. */
export const playsAt = (p: Player, pos: string): boolean => positionsOf(p).includes(pos);

export const BUNDLED_BANK: PlayerBank = {
  season: bundled.season,
  asOf: bundled.generatedAt,
  source: bundled.source,
  byes: bundled.byes as Record<string, number>,
  /*
   * COLLAPSED ON THE WAY IN, not trusted to be clean.
   *
   * The 13 August snapshot shipped fifteen men twice — "Chris Hilton" from the roster CSV and
   * "Chris Hilton Jr" from the player CSV, and in his case on two different teams. The generator
   * has been fixed (`build/fetch-players.mts`) so the next fetch cannot emit them, but the
   * invariant belongs here as well: whatever feed a bank comes from, one man is one row, and the
   * board is what suffers when that is only true by luck.
   */
  /*
   * NO KICKERS, ANYWHERE (Tim, 8 September: "Remove kickers"). This league has never started one
   * and does not draft one, so sixty-six of them were sixty-six rows a man scrolled past on the
   * index, a chip on every filter row that narrowed to nothing he could use, and a line in the
   * tally. `DraftBoard` was already dropping them one screen further along — an omission repeated
   * per screen is a rule the data should have carried in the first place.
   *
   * The bundled snapshot still HAS them, so a season that changes its mind is a filter away
   * rather than a re-fetch; nothing downstream of here sees one. Historic scoring
   * (`boxScores.ts`, `tendencies.ts`) keeps its own K, because what was scored in 2019 is not a
   * question about who may be drafted in 2026.
   */
  players: collapseIdentities((bundled.players as Raw[]).filter((p) => p.p !== 'K').map((p) => ({
    name: p.n, pos: p.p, team: p.t, bye: p.b, status: p.st ?? 'ACT',
    ...(p.last ? { last: p.last } : {}),
    ...(p.e?.length ? { elig: p.e } : {}),
    ...(p.j !== undefined ? { jersey: p.j } : {}),
  }))),
};

export const POSITIONS = ['QB', 'RB', 'WR', 'TE', 'DEF'] as const;

/* -- one man, one row -------------------------------------------------------------------------- */

/**
 * COLLAPSE TWO SPELLINGS OF ONE MAN INTO THE ROW THE BOARD SHOULD SHOW.
 *
 * THE BUG THIS EXISTS FOR, in the form Tim found it: searching "beckh" returned
 *
 *     Odell Beckham       WR  NYG      bye 8
 *     Odell Beckham Jr    WR  ex NYG   free agent
 *
 * — one man, twice, contradicting himself. Sleeper prints him without the suffix and the bundled
 * snapshot with it, so the refresh could not tell that the man it had just placed on a roster was
 * the same man it then carried across as unsigned. The same disagreement between nflverse's two
 * CSVs had already put fifteen more of these into the snapshot itself.
 *
 * A DRAFT BOARD SHOWING ONE PLAYER AS TWO IS WORSE THAN SHOWING HIM WRONG: somebody drafts the
 * second one, and finds out in week one. That sentence is already in `fetch-players.mts`, above a
 * guard that could not see this class of duplicate because it compared names exactly.
 *
 * WHAT MERGES, AND WHAT DELIBERATELY DOES NOT. `sameFeedPlayer` requires the bases to match and
 * the suffixes not to contradict, so a bare name absorbs a suffixed one and "Steve Smith Sr" and
 * "Steve Smith Jr" stay two men. Position is NOT part of the key: the whole failure mode is two
 * feeds disagreeing about a man, and they disagree about position too — keying on it would
 * resurrect exactly the phantom this removes.
 *
 * WHICH ROW WINS. The one that is on a team, then the one carrying a jersey number, because only
 * a current-season roster pass has those and it is the source that is right about where he plays
 * — that is how Chris Hilton, who the player CSV still had in Green Bay, keeps Washington. The
 * NAME is decided separately and the suffix always wins it (`fullerName`), and eligibility is the
 * union, because a man two feeds disagree about is usually a genuine hybrid.
 */
export function collapseIdentities(players: readonly Player[]): Player[] {
  const groups: Array<{ cleaned: ReturnType<typeof cleanPlayerName>; members: Player[] }> = [];
  const byBase = new Map<string, number[]>();

  for (const p of players) {
    const cleaned = cleanPlayerName(p.name);
    const bucket = byBase.get(feedKey(cleaned)) ?? [];
    const at = bucket.find((i) => sameFeedPlayer(groups[i]!.cleaned, cleaned));
    if (at === undefined) {
      bucket.push(groups.length);
      byBase.set(feedKey(cleaned), bucket);
      groups.push({ cleaned, members: [p] });
      continue;
    }
    groups[at]!.members.push(p);
    /* A suffixed spelling makes the GROUP suffixed, so a third bare row still lands here. */
    if (cleaned.suffix && !groups[at]!.cleaned.suffix) groups[at]!.cleaned = cleaned;
  }

  return groups.map(({ members }) => {
    if (members.length === 1) return members[0]!;
    const best = members.reduce((a, b) => (rank(b) > rank(a) ? b : a));
    const name = members.map((m) => m.name).reduce(fullerName);
    const elig = [...new Set(members.flatMap(positionsOf))];
    return {
      ...best,
      name,
      ...(elig.length > 1 ? { elig } : {}),
    };
  });
}

/**
 * On a team beats not; with a jersey beats without. Ties keep the first row seen.
 *
 * A `function`, not a `const` arrow: `BUNDLED_BANK` calls `collapseIdentities` while this module
 * is still evaluating, and an arrow declared below it would be in its temporal dead zone. The
 * whole app imports that constant, so the failure would be a blank screen at boot.
 */
function rank(p: Player): number {
  return (p.team ? 2 : 0) + (p.jersey === undefined ? 0 : 1);
}

/* ------------------------------------------------------------------------------------------ */

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9 ]/g, '');

/**
 * The same normalisation the build script applies. Periods and commas are noise that every feed
 * disagrees about ("A.J. Brown" vs "AJ Brown"); hyphens and apostrophes are part of the name.
 */
export const cleanName = (s: string) => s.replace(/[.,]/g, '').replace(/\s+/g, ' ').trim();

/**
 * Precomputed once per bank rather than per keystroke.
 *
 * `base` is the name with any generational suffix off it, and it is here so the search does not
 * care which side of the "Jr" question the feed came down on: a GM typing the suffix finds a bank
 * row without one, and a GM leaving it off finds a row that has one.
 */
interface Indexed {
  p: Player; key: string; base: string; last: string; initials: string;
  /** Other names this row answers to. Defences only — see `DEF_CLUB`. */
  alias: string;
}

const indexCache = new WeakMap<PlayerBank, Indexed[]>();

/**
 * THE CLUB BEHIND A DEFENCE, for search.
 *
 * The bank names a defence by its nickname — `49ers DEF` — which is what the board should show
 * and is useless to somebody typing "San Francisco". Nobody says "the 49ers defence" out loud;
 * they say "San Fran" or "SF". One line per club, city first, plus the spoken shorthands where
 * a club has one.
 */
const DEF_CLUB: Readonly<Record<string, string>> = {
  ARI: 'arizona cardinals', ATL: 'atlanta falcons', BAL: 'baltimore ravens',
  BUF: 'buffalo bills', CAR: 'carolina panthers', CHI: 'chicago bears',
  CIN: 'cincinnati bengals', CLE: 'cleveland browns', DAL: 'dallas cowboys',
  DEN: 'denver broncos', DET: 'detroit lions', GB: 'green bay packers',
  HOU: 'houston texans', IND: 'indianapolis colts', JAX: 'jacksonville jaguars jags',
  KC: 'kansas city chiefs', LAC: 'los angeles chargers', LAR: 'los angeles rams',
  LV: 'las vegas raiders', MIA: 'miami dolphins', MIN: 'minnesota vikings',
  NE: 'new england patriots pats', NO: 'new orleans saints', NYG: 'new york giants',
  NYJ: 'new york jets', PHI: 'philadelphia eagles', PIT: 'pittsburgh steelers',
  SEA: 'seattle seahawks hawks', SF: 'san francisco 49ers niners forty niners',
  TB: 'tampa bay buccaneers bucs', TEN: 'tennessee titans', WAS: 'washington commanders',
};

function index(bank: PlayerBank): Indexed[] {
  let idx = indexCache.get(bank);
  if (idx) return idx;
  idx = bank.players.map((p) => {
    const key = norm(p.name);
    /*
     * THE SURNAME COMES OFF THE BASE, NOT OFF THE WHOLE NAME. Taking the last token of "odell
     * beckham jr" makes his surname "jr" — so every suffixed player in the league shared one
     * last name, and searching "beckham" dropped him from the last-name tier to the substring
     * tier, behind anybody whose full name merely contained the letters.
     */
    const base = norm(cleanPlayerName(p.name).base);
    const parts = (base || key).split(' ').filter(Boolean);
    /*
     * A defence answers to its city and its club as well as its nickname, and to nothing else:
     * an alias on a person would make "chicago" match every Bear on the roster.
     */
    const alias = p.pos === 'DEF' && p.team
      ? `${DEF_CLUB[p.team.toUpperCase()] ?? ''} ${p.team.toLowerCase()} defense dst`.trim()
      : '';
    return {
      p,
      key,
      base: base || key,
      last: parts[parts.length - 1] ?? key,
      initials: parts.map((w) => w[0]).join(''),
      alias,
    };
  });
  indexCache.set(bank, idx);
  return idx;
}

export interface SearchOptions {
  /** Restrict to these positions. Empty or omitted means all of them. */
  positions?: readonly string[];
  /** Names already off the board. Matched case- and punctuation-insensitively. */
  taken?: Iterable<string>;
  limit?: number;
  /** Leave out free agents and the retired. Off by default; the draft board turns it on. */
  rosteredOnly?: boolean;
}

/**
 * Rank matters more than recall here: the right player should be first, because on draft night
 * people hit enter without reading. Exact name beats last-name-start beats initials beats
 * anything-substring, and ties break on position scarcity — a QB named Smith is a likelier
 * target than the fifth Smith on a practice squad.
 */
export function searchPlayers(
  bank: PlayerBank, query: string, opts: SearchOptions = {},
): Player[] {
  const limit = opts.limit ?? 25;
  const positions = opts.positions?.length ? new Set(opts.positions) : null;
  const taken = new Set([...(opts.taken ?? [])].map(norm));
  const takenBases = new Set(
    [...(opts.taken ?? [])].map((n) => norm(cleanPlayerName(n).base)).filter(Boolean),
  );
  const q = norm(query).trim();
  /* The same query with any suffix taken off, so "odell beckham jr" reaches "Odell Beckham". */
  const qBase = norm(cleanPlayerName(query).base).trim() || q;

  // A `%` turns the whole query into a pattern: `zac% miller` finds Zach and Zack. Scoring
  // collapses to a single tier because "how well does this match" stops being meaningful.
  const wild = hasWildcard(query) ? matcher(query) : null;

  const out: Array<{ p: Player; score: number }> = [];
  for (const it of index(bank)) {
    // Any eligible position counts. Filtering to TE must surface the receiver who also
    // qualifies there, or the filter quietly hides the most interesting names on the board.
    if (positions && !positionsOf(it.p).some((x) => positions.has(x))) continue;
    if (opts.rosteredOnly && isUnsigned(it.p)) continue;
    /* A name already off the board is off it under either spelling. */
    if (taken.has(it.key) || takenBases.has(it.base)) continue;

    let score: number;
    if (wild) {
      if (!wild(it.p.name) && !wild(it.p.team)) continue;
      score = 50;
    } else if (!q) score = 0;
    else if (it.key === q || it.base === qBase) score = 100;
    else if (it.key.startsWith(q) || it.base.startsWith(qBase)) score = 80;
    else if (it.last.startsWith(q)) score = 60;
    else if (q.length >= 2 && it.initials === q) score = 55;
    else if (it.key.includes(q) || it.base.includes(qBase)) score = 30;
    /*
     * A DEFENCE BY ITS CITY OR ITS CODE, at the same tier as a surname. "San Francisco" and
     * "SF" are what the room calls the 49ers; the nickname the bank stores is the one thing
     * nobody says. Scored below an exact or leading name match so a person still wins where
     * both could match — "chargers" reaches the defence, "Justin Herbert" is untouched.
     */
    else if (it.alias && (it.alias.startsWith(q) || it.alias.includes(` ${q}`))) score = 58;
    else if (norm(it.p.team).startsWith(q) && q.length >= 2) score = 10;
    else continue;

    // A player on a roster outranks an equally-good match who is not on one. On draft night the
    // active Josh Allen is always the one you meant.
    out.push({ p: it.p, score: score - (isUnsigned(it.p) ? 5 : 0) });
  }

  out.sort((a, b) => b.score - a.score || a.p.name.localeCompare(b.p.name));
  return out.slice(0, limit).map((x) => x.p);
}

/**
 * Lookup, for reconciling a typed name against the bank after the fact.
 *
 * EXACT FIRST, THEN PAST THE SUFFIX. A keeper saved in 2024 as "Odell Beckham" has to still find
 * him in a bank that now spells him "Odell Beckham Jr", or the record silently stops resolving to
 * a player and the screen shows a name with no team beside it. The exact pass runs first so that
 * where both spellings genuinely exist the typed one still wins.
 */
export function findPlayer(bank: PlayerBank, name: string): Player | undefined {
  const q = norm(name);
  const exact = bank.players.find((p) => norm(p.name) === q);
  if (exact) return exact;
  const c = cleanPlayerName(name);
  return bank.players.find((p) => sameFeedPlayer(cleanPlayerName(p.name), c));
}

/* ------------------------------------------------------------------------------------------ */

/**
 * Refresh from Sleeper, in the browser.
 *
 * Sleeper is used rather than nflverse for exactly one reason: it serves `access-control-allow-
 * origin: *`, so the app can read it directly with no server of our own. Its payload is ~5MB of
 * every player who has ever been in their database, so everything inactive, position-irrelevant
 * or teamless is dropped on the way in — that is what takes it back down to roughly a thousand.
 *
 * Byes are NOT in that feed and are taken from the bundled schedule. That is safe: the 2026
 * schedule was published in May and does not move, whereas rosters churn daily. If a team code
 * ever appears that the schedule does not know, the player keeps a null bye rather than a wrong
 * one — a blank is a question, a wrong number is a mistake somebody acts on.
 */
export async function refreshFromSleeper(
  fetchImpl: typeof fetch = fetch,
  byes: Record<string, number> = BUNDLED_BANK.byes,
): Promise<PlayerBank> {
  const res = await fetchImpl('https://api.sleeper.app/v1/players/nfl');
  if (!res.ok) throw new Error(`Sleeper returned ${res.status}`);
  return bankFromSleeper(await res.json() as Record<string, SleeperPlayer>, byes);
}

interface SleeperPlayer {
  full_name?: string; first_name?: string; last_name?: string;
  position?: string; team?: string | null; status?: string; active?: boolean;
}

const TEAM_FIX: Record<string, string> = { LA: 'LAR', OAK: 'LV', SD: 'LAC', WSH: 'WAS', JAC: 'JAX' };

/** Split out from the fetch so it can be tested without a network. */
export function bankFromSleeper(
  raw: Record<string, SleeperPlayer>, byes: Record<string, number>,
): PlayerBank {
  /* `K` is not in here either — see the note on `BUNDLED_BANK`: a refresh must not put back what
     the snapshot no longer offers, or the pool would gain sixty-six kickers the first time an
     admin pressed Refresh. */
  const wanted = new Set(['QB', 'RB', 'FB', 'WR', 'TE', 'DEF']);
  const seen = new Set<string>();
  const players: Player[] = [];

  /*
   * THE BUNDLED MAN THIS SLEEPER ROW IS, keyed loosely enough to survive a missing "Jr".
   *
   * This lookup is what the refresh was missing. Sleeper spells him "Odell Beckham"; the snapshot
   * spells him "Odell Beckham Jr"; without a key that sees past the suffix the merge placed the
   * first on a roster and then carried the second across as an unsigned free agent, and the board
   * offered a GM the same man twice with contradictory facts.
   */
  const bundledByKey = new Map<string, Player>();
  for (const p of BUNDLED_BANK.players) {
    if (p.pos === 'DEF') continue;
    const key = feedKey(cleanPlayerName(p.name));
    if (!bundledByKey.has(key)) bundledByKey.set(key, p);
  }

  for (const p of Object.values(raw)) {
    if (!p.position || !wanted.has(p.position)) continue;
    // Teamless players are skipped HERE and added back below from the bundled bank. Sleeper
    // carries eleven thousand names and most of the teamless ones are nobody; the bundled list
    // is already curated down to the two seasons that matter.
    if (!p.team) continue;
    if (p.active === false) continue;
    const pos = p.position === 'FB' ? 'RB' : p.position;
    const team = TEAM_FIX[p.team] ?? p.team;
    const name = cleanName(p.full_name ?? `${p.first_name ?? ''} ${p.last_name ?? ''}`);
    if (!name) continue;

    const cleaned = cleanPlayerName(name);
    const key = feedKey(cleaned);
    if (seen.has(key)) continue;
    seen.add(key);

    /*
     * SLEEPER WINS ON THE FACTS, THE SNAPSHOT WINS ON THE NAME. Refreshing is for where he plays
     * and whether he is hurt, which churn daily; how his name is spelled does not churn, and the
     * suffix is the half that distinguishes him from his father. `fullerName` keeps it.
     */
    const known = bundledByKey.get(key);
    const same = known && sameFeedPlayer(cleanPlayerName(known.name), cleaned);
    players.push({
      name: same ? fullerName(known!.name, name) : name,
      pos,
      team,
      bye: byes[team] ?? null,
      status: p.status === 'Inactive' ? 'IR' : 'ACT',
      ...(same && known!.elig?.length ? { elig: known!.elig } : {}),
    });
  }

  // Sleeper's DEF entries are the team abbreviation, not a readable name. Keep the bundled
  // defenses instead — they already read the way people say them out loud.
  const fromBundle = BUNDLED_BANK.players.filter((p) => p.pos === 'DEF');

  /**
   * Free agents, added back.
   *
   * A refresh used to DELETE them. Sleeper gives an unsigned player no team, the loop above drops
   * anyone without one, and so Nick Chubb was in the bank until somebody hit refresh and then was
   * not — on a board whose whole job is to list everybody draftable. Somebody takes a flier on an
   * unsigned running back in the last round every single year.
   *
   * So: anyone in the curated bundled list whom Sleeper does not place on a team is carried
   * across as a free agent, keeping his last club for identification. Sleeper still wins wherever
   * the two disagree, which is the point of refreshing.
   */
  /*
   * KEYED WITHOUT THE POSITION, and that is not a shortcut.
   *
   * It used to be `name|position`, which needs the two feeds to agree about BOTH — and the whole
   * reason this merge is delicate is that they agree about neither. nflverse has Justin Shorter
   * at receiver and at tight end depending which of its CSVs you read; a position disagreement
   * therefore resurrected a man Sleeper had just placed on a roster, as a free agent, under the
   * other feed's spelling. One name is one man here.
   *
   * `collapseIdentities` below would clean that up either way — this does not show up in a test,
   * and it is kept because not building the phantom is better than building it and sweeping up.
   */
  const onATeam = new Set(players.map((p) => feedKey(cleanPlayerName(p.name))));
  const unsigned = BUNDLED_BANK.players
    .filter((p) => p.pos !== 'DEF' && !onATeam.has(feedKey(cleanPlayerName(p.name))))
    .map((p) => ({
      ...p,
      team: '',
      bye: null,
      status: p.status === 'RET' || p.status === 'PS' ? p.status : 'FA',
      last: p.last ?? (p.team || undefined),
    }));

  /*
   * Eligibility came across with the name above — Sleeper reports one position per player and
   * knows nothing about hybrids, so a refresh would otherwise flatten every one of them to a
   * single chip.
   *
   * COLLAPSED ONE LAST TIME. Everything above is careful, and the invariant is still asserted
   * rather than assumed: three lists are being concatenated here and the board must never offer
   * the same man twice, whatever any feed does next season.
   */
  const merged = collapseIdentities(
    [...players.filter((p) => p.pos !== 'DEF'), ...unsigned, ...fromBundle],
  );
  merged.sort((a, b) => a.name.localeCompare(b.name));

  return {
    season: BUNDLED_BANK.season,
    asOf: new Date().toISOString().slice(0, 10),
    source: 'Sleeper (live refresh); byes from the bundled schedule',
    byes,
    players: merged,
  };
}
